import { DocumentNode, gql } from '@apollo/client';
import { Text, VStack } from '@chakra-ui/react';
import * as React from 'react';

import { Card } from '@/imports/ui/chakra/components/Card';

import { QuestionDetail_QuestionV2Fragment } from './graphql-types/QuestionDetail_QuestionV2Fragment';

import { DeletedHeader } from '../components/DeletedHeader';
import { Moderation } from '../components/Moderation';
import { ModerationHeader } from '../components/ModerationHeader';
import { PinnedHeader } from '../components/PinnedHeader';
import { Question } from '../components/Question';
import { useModeration } from '../hooks/useModeration';
import { AnswerDetail } from './components/AnswerDetail';

export const QuestionDetail: React.FC<
  {
    question: QuestionDetail_QuestionV2Fragment;
  } & { options?: { isExpandedByDefault?: boolean } }
> & {
  questionFragment: DocumentNode;
} = ({ question, options }) => {
  const { shouldHidePost, shouldShowHeader } = useModeration({
    post: question,
    isAnswerHeader: false,
  });

  return shouldHidePost ? (
    <Moderation post={question} />
  ) : (
    <>
      <Card w="full">
        {question.deleted && (
          <DeletedHeader post={question} isAnswerHeader={false} />
        )}
        {shouldShowHeader && (
          <ModerationHeader post={question} isAnswerHeader={false} />
        )}
        <PinnedHeader post={question} />
        <Question
          question={question}
          options={{
            hideSeeAllAnswers: true,
            isExpandedByDefault: options?.isExpandedByDefault,
          }}
        />
      </Card>
      {!!question.answers?.length && (
        <>
          <Text color="gray.600" fontWeight="semibold" fontSize="sm" py="6">
            ANSWERS
          </Text>
          <VStack spacing="2">
            {question.answers.map((answer) => (
              <AnswerDetail key={answer._id} answer={answer} />
            ))}
          </VStack>
        </>
      )}
    </>
  );
};

QuestionDetail.questionFragment = gql`
  fragment QuestionDetail_QuestionV2Fragment on QuestionV2 {
    _id
    deleted
    ...Question_QuestionV2Fragment
    ...useModeration_PostFragment
    ...ModerationHeader_PostFragment
    ...Moderation_PostFragment
    ...PinnedHeader_PostFragment
    answers {
      _id
      ...AnswerDetail_AnswerV2Fragment
    }
  }
  ${Question.questionFragment}
  ${useModeration.postFragment}
  ${ModerationHeader.postFragment}
  ${Moderation.postFragment}
  ${PinnedHeader.postFragment}
  ${AnswerDetail.answerFragment}
`;
